/**
 * Typed, null-safe wrapper over the Yandex Metrika counter created by the inline
 * loader in index.html. The counter may not be loaded yet (or at all, if blocked),
 * so every call is guarded.
 */
interface YandexMetrika {
  reachGoal(goal: string): void;
  userParams(params: object): void;
}

declare global {
  interface Window {
    yandexMetrika?: YandexMetrika;
    /**
     * The loader's callback queue (see the Metrika snippet in index.html). watch.js
     * runs each entry once it loads; the snippet's own entry creates the counter, so
     * anything queued here runs after `window.yandexMetrika` exists.
     */
    yandex_metrika_callbacks?: Array<() => void>;
  }
}

// Goals = discrete events worth counting. Theme ADOPTION is a persistent,
// segmentable visitor attribute instead — see setUserParams.
export type Goal =
  | "player-won"
  | "player-lost"
  | "theme-set-dark" // manual opt-in to dark
  | "theme-revert-auto-dark"; // turned off an auto-applied dark theme

export function reachGoal(goal: Goal): void {
  withMetrika((ym) => ym.reachGoal(goal));
}

/**
 * Run `fn` with the counter, tolerating the load race. The counter loads
 * asynchronously (watch.js in index.html), so at module-load time — e.g. the
 * initial theme `userParams` call from `initTheme` — `window.yandexMetrika` is
 * usually still undefined and a direct call would be silently dropped. If it's
 * ready, call now; otherwise queue on the loader's `yandex_metrika_callbacks`
 * array so the call runs once the counter exists. If the loader never resolves
 * (e.g. blocked), the queued call simply never fires — same as before.
 */
function withMetrika(fn: (ym: YandexMetrika) => void): void {
  if (window.yandexMetrika) {
    fn(window.yandexMetrika);
    return;
  }
  if (!window.yandex_metrika_callbacks) {
    window.yandex_metrika_callbacks = [];
  }
  window.yandex_metrika_callbacks.push(() => {
    if (window.yandexMetrika) fn(window.yandexMetrika);
  });
}

/**
 * Persistent visitor parameters — segmentable across all Metrika reports, so the
 * theme's adoption and its usefulness (retention/engagement per segment) can be
 * read, which goals can't show.
 */
export interface ThemeUserParams {
  theme: "light" | "dark";
  themeSource: "auto" | "manual";
}

export function setUserParams(params: ThemeUserParams): void {
  withMetrika((ym) => ym.userParams(params));
}
